import {
  getRacePersonaFromStorage,
  getAttrMod,
  getLivingSpace,
  getRaceComboHonorMod,
  getConstructionCost,
  getRazeCost,
  getRitualModInc,
  getRitualModDec,
  getCurrentDraftLevelFactor,
  getBaseME,
  getModOff,
  getModDef,
  getRawOff,
  getRawDef,
  getDiminishedBuildingMod,
  getDiminishedBuildingEffect,
  calculateBE,
  calculateRunesPerTick,
  calculateBushelsPerTick,
  getBanksBonusGc,
  calculateModIncome,
  calculateWages,
  getOME,
  getDME,
} from './Calculations';
import {
  BASE_THIEF_COST,
  BASE_SPEC_COST,
  BASE_ELITE_COST,
  HOME_BONUS,
  PAT_DRAFT_MOD,
  draftRates,
  DEFAULT_MOD,
} from './GameAgeData';
import { calcTrainingTick } from './TrainingCalcs';
import {
  ArmyCalc,
  BookType,
  CalculateBeProps,
  CalculateWages,
  CalculatedEconomy,
  DraftRate,
  Ritual,
  RitualModifier,
  TrainingCalcProps,
} from '../@types/Calculations';
import { RacePersona } from '../@types/RacesResponse';
import { SimState, StateCalculation } from '../@types/State';

export const getGameState = (state: SimState) => {
  const { acres, be, raceCombo, science, title, training } = state;

  function asPercent(value: number) {
    return value / 100;
  }

  function getSpellMod(spell: string, mod: number) {
    return hasSpell(spell) ? mod : DEFAULT_MOD;
  }

  function numberBuildings(buildingType: string) {
    return Math.floor((acres * buildPercentages[buildingType]) / 100);
  }

  function hasSpell(spell: string) {
    return state.spells?.includes(spell) ?? false;
  }

  function sciModIncrease(sciType: BookType) {
    const mod = science[sciType]
      ? 1 + asPercent(science[sciType])
      : DEFAULT_MOD;
    return mod;
  }

  function sciModDecrease(sciType: BookType) {
    const mod = science[sciType]
      ? 1 - asPercent(science[sciType])
      : DEFAULT_MOD;
    return mod;
  }

  function getRitMod(mod: RitualModifier, direction: '+' | '-') {
    return direction === '+'
      ? getRitualModInc(mod, ritual, ritEff)
      : getRitualModDec(mod, ritual, ritEff);
  }

  const buildPercentages = state.buildPercentages ?? state.build;
  const ritEff = state.ritEff;
  const persona: string =
    raceCombo?.substring(raceCombo?.indexOf(' ') + 1) ?? 'Mystic';
  const race: string =
    raceCombo?.substring(0, raceCombo?.indexOf(' ')) ?? 'Faery';
  const racePersona: RacePersona | undefined = getRacePersonaFromStorage(
    race,
    persona
  );
  const ritual: Ritual = JSON.parse(state.ritual);
  const draftrate: DraftRate = JSON.parse(state.draftRate);
  const armyCalc: ArmyCalc = {
    soldiers: state.soldiers,
    oSpecs: state.oSpecs,
    dSpecs: state.dSpecs,
    elites: state.elites,
    horses: state.horses,
    thieves: state.thieves,
    prisoners: state.prisoners,
    wizards: state.wizards,
    soldiersTraining: state.soldiersTraining,
    total:
      state.oSpecs +
      state.dSpecs +
      state.elites +
      state.soldiers +
      state.soldiersTraining,
  };

  const numberBanks = numberBuildings('banks');
  const numberTowers = numberBuildings('towers');
  const numberFarms = numberBuildings('farms');
  const numberHomes = numberBuildings('homes');
  const numberBarren = numberBuildings('barren');
  const numberGuilds = numberBuildings('guilds');

  const homesBonus =
    numberHomes * (HOME_BONUS + getAttrMod(racePersona, 'HomesBonus', 0));

  // raceCombo Mods
  const raceIncomeMod = getAttrMod(racePersona, 'IncomeMod', DEFAULT_MOD);
  const raceOmeMod = getAttrMod(racePersona, 'OmeMod', DEFAULT_MOD);
  const raceDmeMod = getAttrMod(racePersona, 'DmeMod', DEFAULT_MOD);
  const eliteCostRaw = getAttrMod(racePersona, 'EliteCost', BASE_ELITE_COST);
  const thiefCostRaw = getAttrMod(racePersona, 'ThievesCost', BASE_THIEF_COST);
  const defSpecCostRaw = getAttrMod(racePersona, 'DefSpecCost', BASE_SPEC_COST);
  const offSpecCostRaw = getAttrMod(racePersona, 'OffSpecCost', BASE_SPEC_COST);

  // Honor Mods
  const rankOmeMod = getRaceComboHonorMod('me', title, racePersona);
  const rankIncomeModifier = getRaceComboHonorMod('income', title, racePersona);

  // science modifiers
  const tacticsMod = sciModIncrease('tactics');
  const toolsMod = sciModIncrease('tools');
  const strategyMod = sciModIncrease('strategy');
  const artisanModifier = sciModDecrease('artisan');
  const bookkeepingMod = sciModDecrease('bookkeeping');
  const productionMod = sciModIncrease('production');
  const alchemyMod = sciModIncrease('alchemy');
  const heroismRateMod = sciModIncrease('heroism');
  const heroismCostMod = sciModDecrease('heroism');
  const housingMod = sciModIncrease('housing');

  // ritual Mods
  const ritOmeMod = getRitMod('omeModifier', '+');
  const ritBeMod = getRitMod('beModifier', '+');
  const ritIncomeMod = getRitMod('incomeModifier', '+');
  const ritDmeMod = getRitMod('dmeModifier', '+');
  const ritConstructMod = getRitMod('constructionModifier', '-');
  const ritTrainingMod = getRitMod('trainingCostModifer', '-');
  const ritWagesMod = getRitMod('wagesModifier', '-');

  // population
  const { modLivingSpace, rawLivingSpace } = getLivingSpace({
    homesBonus,
    sciMod: housingMod,
    built: acres - numberBarren,
    barrenCount: numberBarren,
    title: title,
    racePersona,
  });

  // BE
  const beCalcProps: CalculateBeProps = {
    acres,
    numberHomes,
    modLivingSpace,
    armyCalc,
    toolsMod,
    ritBeMod,
  };
  const calculatedEconomy: CalculatedEconomy = calculateBE(beCalcProps);

  const BEToUse = be ? asPercent(be) : calculatedEconomy.estBE;

  // Building effects after BE

  const banksIncomeModifier = getDiminishedBuildingMod(
    buildPercentages.banks,
    acres,
    'BANK_INCOME_LEVEL',
    BEToUse,
    '+'
  );
  const armsDraftModifier = getDiminishedBuildingMod(
    buildPercentages.armouries,
    acres,
    'ARMOURY_MILITARY_WAGES_AND_DRAFT_COST',
    BEToUse,
    '-'
  );
  const armsWagesModifier = getDiminishedBuildingMod(
    buildPercentages.armouries,
    acres,
    'ARMOURY_MILITARY_WAGES_AND_DRAFT_COST',
    BEToUse,
    '-'
  );

  const armsTrainingModifier = getDiminishedBuildingMod(
    buildPercentages.armouries,
    acres,
    'ARMOURY_MILITARY_TRAINING_COST',
    BEToUse,
    '-'
  );

  const millsModifier = getDiminishedBuildingMod(
    buildPercentages.mills,
    acres,
    'MILL_BUILDING_COST',
    BEToUse,
    '-'
  );

  const trainingGroundsOMEBonus =
    getDiminishedBuildingEffect(
      buildPercentages.trainingGrounds,
      acres,
      'TRAINING_GROUND_OFFENSIVE_EFFICIENCY',
      BEToUse
    ) * 100;

  const fortsDMEBonus =
    getDiminishedBuildingEffect(
      buildPercentages.forts,
      acres,
      'FORT_DEFENSIVE_EFFICIENCY',
      BEToUse
    ) * 100;

  const castlesProtectionBonus =
    getDiminishedBuildingEffect(
      buildPercentages.castles,
      acres,
      'CASTLE_PROTECT_RESOURCE_UNDER_ATTACK',
      BEToUse
    ) * 100;

  // income
  const runesPerTick = calculateRunesPerTick(numberTowers, productionMod);

  const modFoodProduction = calculateBushelsPerTick(
    acres,
    numberFarms,
    numberBarren,
    BEToUse,
    productionMod,
    hasSpell('fl')
  );

  const gcFromBanks = getBanksBonusGc(numberBanks, BEToUse);

  const { modIncome, rawIncome } = calculateModIncome({
    employed: calculatedEconomy.employed,
    unemployed: calculatedEconomy.unemployed,
    prisoners: armyCalc.prisoners,
    banksBonusGc: gcFromBanks,
    banksIncomeModifier,
    alchemyMod,
    rankIncomeModifier,
    raceIncomeMod,
    ritIncomeMod,
  });

  const wages = calculateWages({
    wagesPcnt: state.wages,
    armyCalc,
    hasHI: hasSpell('hi'),
    hasIA: hasSpell('ia'),
    hasGreed: hasSpell('gre'),
    armsWagesModifier,
    ritWagesMod,
    bookkeepingMod,
  } as CalculateWages);

  // draft
  const raceComboDraftMod = DEFAULT_MOD;
  const modDraftSpeed =
    (draftrate?.speed *
      getSpellMod('pat', PAT_DRAFT_MOD) *
      raceComboDraftMod *
      heroismRateMod) /
    100;

  const maxDraftSpeed =
    (draftRates[4].speed * getSpellMod('pat', PAT_DRAFT_MOD) * heroismRateMod) /
    100;

  const currentDraftLevelFactor = getCurrentDraftLevelFactor(
    armyCalc.total / modLivingSpace
  );

  const draftCostPerSold = Math.floor(
    currentDraftLevelFactor *
      draftrate.cost *
      heroismCostMod *
      armsDraftModifier
  ); // TODO no race/personality bonus

  const wizardsperTick = (numberGuilds * 0.2) / 10;
  const soldiersPerTick = Math.floor(
    calculatedEconomy.peasants * modDraftSpeed
  );
  const soldiersPerTickMax = Math.floor(
    calculatedEconomy.peasants * maxDraftSpeed
  );
  const targetMilitary = Math.floor(
    modLivingSpace * asPercent(state.targetDraft)
  );
  const recruitsNeeded = targetMilitary - armyCalc.total - armyCalc.thieves;

  const draftTicks = Math.ceil(
    Math.log(1 - recruitsNeeded / calculatedEconomy.peasants) /
      Math.log(1 - modDraftSpeed)
  );
  const draftTicksMin = Math.ceil(
    Math.log(1 - recruitsNeeded / calculatedEconomy.peasants) /
      Math.log(1 - maxDraftSpeed)
  );

  const draftCostPerTick = soldiersPerTick * draftCostPerSold;
  const netIncome = Math.floor(modIncome - wages - draftCostPerTick);

  // military
  const baseME = getBaseME(state.wages);

  const oME = getOME(
    baseME,
    trainingGroundsOMEBonus,
    rankOmeMod,
    tacticsMod,
    raceOmeMod,
    ritOmeMod,
    hasSpell('fan'),
    hasSpell('bl')
  );

  const dME = getDME(
    baseME,
    fortsDMEBonus,
    strategyMod,
    raceDmeMod,
    ritDmeMod,
    hasSpell('fan'),
    hasSpell('mp'),
    hasSpell('gp')
  );

  const rawOff = getRawOff(racePersona, armyCalc, hasSpell('agr'));
  const rawDef = getRawDef(racePersona, armyCalc, hasSpell('agr'));
  const modOff = getModOff(rawOff, oME);
  const modDef = getModDef(rawDef, dME, acres);

  const constructionCost = getConstructionCost(
    acres,
    millsModifier,
    ritConstructMod,
    artisanModifier
  );
  const razeCost = getRazeCost(acres, artisanModifier);
  const thiefCost = Math.floor(thiefCostRaw * ritTrainingMod);
  const troopCostModifier = ritTrainingMod * armsTrainingModifier;
  const eliteCost = Math.max(Math.floor(eliteCostRaw * troopCostModifier), 0);
  const dSpecCost = Math.floor(defSpecCostRaw * troopCostModifier);
  const oSpecCost = Math.floor(offSpecCostRaw * troopCostModifier);

  const trainingProps: TrainingCalcProps = {
    acres,
    training,
    razeCost,
    constructionCost,
    armyCalc,
    thiefCost,
    dSpecCost,
    oSpecCost,
    eliteCost,
    existingMoney: state.money,
    recruitsNeeded,
  };
  const trainingCalc = calcTrainingTick(trainingProps);

  const calcState: StateCalculation = {
    acres,
    armyCalc,
    calculatedEconomy,
    beUsed: BEToUse * 100,
    estBE: Math.floor(calculatedEconomy.estBE * 10000) / 100,
    modLivingSpace,
    rawLivingSpace,
    gcFromBanks,
    bushelsPerTick: Math.floor(modFoodProduction),
    runesPerTick: Math.floor(runesPerTick),
    rawIncome,
    modIncome,
    netIncome,
    wages,
    draftCost: draftCostPerTick,
    dME,
    oME,
    baseME,
    rawOff,
    rawDef,
    modOff,
    modDef,
    modDraftSpeed,
    maxDraftSpeed,
    targetDraft: state.targetDraft,
    trainingGroundsOMEBonus: Math.floor(trainingGroundsOMEBonus * 1000) / 1000,
    fortsDMEBonus: Math.floor(fortsDMEBonus * 1000) / 1000,
    homesBonus,
    castlesProtectionBonus: Math.floor(castlesProtectionBonus * 1000) / 1000,
    armsDraftModifier,
    armsWagesModifier,
    armsTrainingModifier,
    banksIncomeModifier,
    tacticsModifier: tacticsMod,
    currentDraftLevelFactor: Math.floor(currentDraftLevelFactor * 1000) / 1000,
    draftCostPerSold,
    targetMilitary,
    trainingCalc,
    recruitsNeeded,
    wizardsperTick: Math.floor(wizardsperTick * 1000) / 1000,
    soldiersPerTick,
    soldiersPerTickMax,
    draftTicks,
    draftTicksMin,
    constructionCost,
    razeCost,
    money: state.money,
    thiefCost,
    dSpecCost,
    oSpecCost,
    eliteCost,
  };
  return calcState;
};
